import { useEffect, useState } from "react";
import apiClient from "../../../../shared/api/client";
import { mapTaskTemplateCategory } from "../mappers";

export default function AuditTaskTemplatesPage({ showToast }) {
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditTypes, setAuditTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState("");
  const [typeMsg, setTypeMsg] = useState(null);
  const [categoryDraft, setCategoryDraft] = useState({ name: "", description: "" });
  const [templateDrafts, setTemplateDrafts] = useState({});

  // StatusValue state
  const [statusValues, setStatusValues] = useState([]);
  const [statusDraft, setStatusDraft] = useState({ name: "", color: "#60a5fa" });
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editingStatusDraft, setEditingStatusDraft] = useState({});

  useEffect(() => {
    loadData();
    apiClient.getUsers().then((data) => setUsers(data.users || data || [])).catch(() => {});
    apiClient.getAuditTypes().then((data) => setAuditTypes(data || [])).catch(() => {});
    loadStatusValues();
  }, []);

  async function loadData() {
    const data = await apiClient.getTaskTemplateCategories();
    setCategories(data.map(mapTaskTemplateCategory));
  }

  async function loadStatusValues() {
    try {
      const data = await apiClient.getStatusValues("audit");
      setStatusValues(data);
    } catch { /* swallow */ }
  }

  // ── Category handlers ──
  async function createCategory() {
    if (!categoryDraft.name.trim()) return;
    await apiClient.createTaskTemplateCategory(categoryDraft);
    setCategoryDraft({ name: "", description: "" });
    await loadData();
    showToast("Category created", "success");
  }

  async function saveCategory(category) {
    await apiClient.updateTaskTemplateCategory(category.id, {
      name: category.name,
      description: category.description,
      active: category.active,
    });
    await loadData();
    showToast("Category saved", "success");
  }

  async function removeCategory(categoryId) {
    if (!window.confirm("Delete this category and its templates?")) return;
    await apiClient.deleteTaskTemplateCategory(categoryId);
    await loadData();
    showToast("Category deleted", "success");
  }

  async function createTemplate(categoryId) {
    const draft = templateDrafts[categoryId];
    if (!draft?.title?.trim()) return;
    await apiClient.createTaskTemplate({
      categoryId,
      title: draft.title,
      description: draft.description,
      sortOrder: Number(draft.sortOrder || 0),
      defaultAssigneeId: draft.defaultAssigneeId || null,
      defaultDueOffsetDays: draft.defaultDueOffsetDays === "" ? null : Number(draft.defaultDueOffsetDays || 0),
    });
    setTemplateDrafts((prev) => ({ ...prev, [categoryId]: { title: "", description: "", sortOrder: "", defaultAssigneeId: "", defaultDueOffsetDays: "" } }));
    await loadData();
    showToast("Template task created", "success");
  }

  async function saveTemplate(template) {
    await apiClient.updateTaskTemplate(template.id, {
      categoryId: template.categoryId,
      title: template.title,
      description: template.description,
      sortOrder: Number(template.sortOrder || 0),
      defaultAssigneeId: template.defaultAssigneeId || null,
      defaultDueOffsetDays: template.defaultDueOffsetDays === "" ? null : Number(template.defaultDueOffsetDays || 0),
      active: template.active,
    });
    await loadData();
    showToast("Template saved", "success");
  }

  async function removeTemplate(templateId) {
    if (!window.confirm("Delete this template task?")) return;
    await apiClient.deleteTaskTemplate(templateId);
    await loadData();
    showToast("Template deleted", "success");
  }

  function updateCategoryLocal(categoryId, patch) {
    setCategories((prev) => prev.map((category) => category.id === categoryId ? { ...category, ...patch } : category));
  }

  function updateTemplateLocal(categoryId, templateId, patch) {
    setCategories((prev) => prev.map((category) => (
      category.id !== categoryId ? category : {
        ...category,
        templates: category.templates.map((template) => template.id === templateId ? { ...template, ...patch } : template),
      }
    )));
  }

  // ── StatusValue handlers ──
  async function createStatus() {
    if (!statusDraft.name.trim()) return;
    try {
      await apiClient.createStatusValue({ ...statusDraft, module: "audit" });
      setStatusDraft({ name: "", color: "#60a5fa" });
      await loadStatusValues();
      showToast("Status created", "success");
    } catch (e) { showToast(e.message || "Failed", "error"); }
  }

  async function saveStatusEdit(id) {
    try {
      await apiClient.updateStatusValue(id, editingStatusDraft);
      setEditingStatusId(null);
      await loadStatusValues();
      showToast("Status updated", "success");
    } catch (e) { showToast(e.message || "Failed", "error"); }
  }

  async function deleteStatus(id) {
    if (!window.confirm("Delete this status? Audit cards currently using it will show as 'Unknown'.")) return;
    try {
      await apiClient.deleteStatusValue(id);
      await loadStatusValues();
      showToast("Status deleted", "success");
    } catch (e) { showToast(e.message || "Failed", "error"); }
  }

  async function moveStatus(index, direction) {
    const next = [...statusValues];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= next.length) return;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setStatusValues(next); // optimistic
    try {
      await apiClient.reorderStatusValues("audit", next.map(s => s.id));
    } catch (e) {
      await loadStatusValues(); // rollback
      showToast("Reorder failed", "error");
    }
  }

  async function createAuditType() {
    if (!newTypeName.trim()) return;
    try {
      const type = await apiClient.createAuditType(newTypeName.trim());
      setAuditTypes((current) => [...current, type].sort((a, b) => a.name.localeCompare(b.name)));
      setNewTypeName("");
      setTypeMsg({ type: "ok", text: `Created "${type.name}"` });
      showToast("Audit type created", "success");
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || "Failed to create audit type";
      setTypeMsg({ type: "err", text: message });
      showToast(message, "error");
    }
  }

  async function removeAuditType(type) {
    try {
      await apiClient.deleteAuditType(type.id);
      setAuditTypes((current) => current.filter((item) => item.id !== type.id));
      setTypeMsg({ type: "ok", text: `Deleted "${type.name}"` });
      showToast("Audit type deleted", "success");
    } catch (error) {
      const message = error?.response?.data?.error || "Cannot delete - type may be in use";
      setTypeMsg({ type: "err", text: message });
      showToast(message, "error");
    }
  }

  return (
    <div className="fade flex flex-col">
      <div className="pg-top">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="pg-title">Audit Task Templates</div>
            <div className="pg-sub">Manage reusable task categories, predefined audit tasks, and status values.</div>
          </div>
        </div>
      </div>

      <div className="pg-body flex flex-col gap-[18px]">
        <div className="bg-rc-surface border border-rc-border rounded-[14px] p-4" style={{marginBottom: 8}}>
          <div className="flex justify-between gap-3 items-start mb-3" style={{marginBottom: 12}}>
            <div>
              <div style={{fontSize: 14, fontWeight: 700, color: '#dce8f8'}}>Audit Types</div>
              <div style={{fontSize: 11, color: '#6889b4', marginTop: 2}}>Manage the audit type options used when creating and filtering audits.</div>
            </div>
          </div>

          {typeMsg && <div className={`team-alert ${typeMsg.type === "ok" ? "ok" : "err"}`} style={{marginBottom: 12}}>{typeMsg.text}</div>}

          <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:14}}>
            {auditTypes.map((type) => (
              <div key={type.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'8px 10px',background:'rgba(10,21,40,.5)',border:'1px solid #14213d',borderRadius:8}}>
                <span style={{fontSize:12.5,color:'#dce8f8'}}>{type.name}</span>
                <button className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]" onClick={() => removeAuditType(type)}>✕</button>
              </div>
            ))}
            {auditTypes.length === 0 && (
              <div style={{textAlign:'center',padding:'16px 0',color:'#4a6280',fontSize:12}}>No audit types yet. Add one below.</div>
            )}
          </div>

          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <input
              className="w-full bg-[#070e20] border border-[#14213d] rounded-md px-3 py-2 text-[12px] text-rc-text-primary outline-none transition-colors font-sans focus:border-rc-accent"
              style={{flex:1}}
              placeholder="Audit type name"
              value={newTypeName}
              onChange={(e)=>setNewTypeName(e.target.value)}
              onKeyDown={(e)=>{ if (e.key === 'Enter') createAuditType(); }}
            />
            <button className="bg-rc-accent border-0 text-white rounded-md px-5 py-1.5 text-xs font-bold cursor-pointer font-sans transition-colors hover:bg-[#2563eb] disabled:opacity-40 disabled:cursor-not-allowed" onClick={createAuditType}>Add Type</button>
          </div>
        </div>

        {/* ── Status Values Section ── */}
        <div className="bg-rc-surface border border-rc-border rounded-[14px] p-4" style={{marginBottom: 8}}>
          <div className="flex justify-between gap-3 items-start mb-3" style={{marginBottom: 12}}>
            <div>
              <div style={{fontSize: 14, fontWeight: 700, color: '#dce8f8'}}>Status Values</div>
              <div style={{fontSize: 11, color: '#6889b4', marginTop: 2}}>Manage audit board stage labels and colors. Drag order controls board column order.</div>
            </div>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:14}}>
            {statusValues.map((sv, index) => (
              <div key={sv.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',background:'rgba(10,21,40,.5)',border:'1px solid #14213d',borderRadius:8}}>
                {/* Color dot */}
                <span style={{width:12,height:12,borderRadius:'50%',background:sv.color,flexShrink:0,display:'inline-block'}}/>
                {editingStatusId === sv.id ? (
                  <>
                    <input
                      style={{flex:1,background:'transparent',border:'1px solid #253550',borderRadius:6,padding:'4px 8px',color:'#dce8f8',fontSize:12}}
                      value={editingStatusDraft.name ?? sv.name}
                      onChange={e=>setEditingStatusDraft(p=>({...p,name:e.target.value}))}
                    />
                    <input
                      type="color"
                      style={{width:28,height:28,borderRadius:4,border:'1px solid #253550',background:'transparent',cursor:'pointer',padding:0}}
                      value={editingStatusDraft.color ?? sv.color}
                      onChange={e=>setEditingStatusDraft(p=>({...p,color:e.target.value}))}
                    />
                    <button className="self-end bg-[rgba(59,127,250,0.15)] border border-[rgba(59,127,250,0.3)] text-rc-accent-strong rounded-md px-4 py-1.5 text-xs font-semibold cursor-pointer transition-colors font-sans hover:bg-[rgba(59,127,250,0.25)]" onClick={()=>saveStatusEdit(sv.id)}>Save</button>
                    <button className="bg-transparent border border-rc-border text-rc-text-secondary rounded-md px-4 py-1.5 text-xs font-semibold cursor-pointer transition-colors font-sans hover:bg-rc-surface-hover" onClick={()=>setEditingStatusId(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{flex:1,fontSize:12.5,color:'#dce8f8'}}>{sv.name}</span>
                    <button className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]" style={{fontSize:9.5,padding:'3px 7px',background:'rgba(59,127,250,.08)',color:'#60a5fa',border:'1px solid rgba(59,127,250,.18)',borderRadius:5}}
                      onClick={()=>{setEditingStatusId(sv.id);setEditingStatusDraft({name:sv.name,color:sv.color});}}>
                      Edit
                    </button>
                  </>
                )}
                {/* Reorder */}
                <button
                  className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]"
                  disabled={index === 0}
                  style={{opacity:index===0?.3:1,fontSize:10}}
                  onClick={()=>moveStatus(index,-1)}
                  title="Move up"
                >↑</button>
                <button
                  className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]"
                  disabled={index === statusValues.length-1}
                  style={{opacity:index===statusValues.length-1?.3:1,fontSize:10}}
                  onClick={()=>moveStatus(index,1)}
                  title="Move down"
                >↓</button>
                <button className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]" onClick={()=>deleteStatus(sv.id)}>✕</button>
              </div>
            ))}
            {statusValues.length === 0 && (
              <div style={{textAlign:'center',padding:'16px 0',color:'#4a6280',fontSize:12}}>No status values yet. Add one below.</div>
            )}
          </div>

          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <input
              className="w-full bg-[#070e20] border border-[#14213d] rounded-md px-3 py-2 text-[12px] text-rc-text-primary outline-none transition-colors font-sans focus:border-rc-accent"
              style={{flex:1}}
              placeholder="Status name (e.g. In Progress)"
              value={statusDraft.name}
              onChange={e=>setStatusDraft(p=>({...p,name:e.target.value}))}
              onKeyDown={e=>{if(e.key==='Enter') createStatus();}}
            />
            <input
              type="color"
              style={{width:36,height:36,borderRadius:6,border:'1px solid #253550',background:'transparent',cursor:'pointer',padding:2}}
              value={statusDraft.color}
              onChange={e=>setStatusDraft(p=>({...p,color:e.target.value}))}
            />
            <button className="bg-rc-accent border-0 text-white rounded-md px-5 py-1.5 text-xs font-bold cursor-pointer font-sans transition-colors hover:bg-[#2563eb] disabled:opacity-40 disabled:cursor-not-allowed" onClick={createStatus}>Add Status</button>
          </div>
        </div>

        {/* ── Template Categories ── */}
        <div className="grid grid-cols-[1.2fr_1.5fr_auto] gap-2.5 items-center max-xl:grid-cols-1">
          <input className="w-full bg-[#070e20] border border-[#14213d] rounded-md px-3 py-2 text-[12px] text-rc-text-primary outline-none transition-colors font-sans focus:border-rc-accent" placeholder="Category name" value={categoryDraft.name} onChange={(e)=>setCategoryDraft((prev)=>({ ...prev, name: e.target.value }))} />
          <input className="w-full bg-[#070e20] border border-[#14213d] rounded-md px-3 py-2 text-[12px] text-rc-text-primary outline-none transition-colors font-sans focus:border-rc-accent" placeholder="Description (optional)" value={categoryDraft.description} onChange={(e)=>setCategoryDraft((prev)=>({ ...prev, description: e.target.value }))} />
          <button className="bg-rc-accent border-0 text-white rounded-md px-5 py-1.5 text-xs font-bold cursor-pointer font-sans transition-colors hover:bg-[#2563eb] disabled:opacity-40 disabled:cursor-not-allowed" onClick={createCategory}>Create Category</button>
        </div>

        <div className="flex flex-col gap-3.5">
          {categories.map((category) => (
            <div key={category.id} className="bg-rc-surface border border-rc-border rounded-[14px] p-4">
              <div className="flex justify-between gap-3 items-start mb-3">
                <div className="grid grid-cols-[1fr_1.2fr] gap-2.5 flex-1 max-xl:grid-cols-1">
                  <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={category.name} onChange={(e)=>updateCategoryLocal(category.id, { name: e.target.value })} />
                  <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={category.description} placeholder="Description" onChange={(e)=>updateCategoryLocal(category.id, { description: e.target.value })} />
                </div>
                <div className="flex gap-2 items-center">
                  <button className="self-end bg-[rgba(59,127,250,0.15)] border border-[rgba(59,127,250,0.3)] text-rc-accent-strong rounded-md px-4 py-1.5 text-xs font-semibold cursor-pointer transition-colors font-sans hover:bg-[rgba(59,127,250,0.25)]" onClick={()=>saveCategory(category)}>Save</button>
                  <button className="bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] text-[#f87171] rounded-md px-4 py-1.5 text-xs font-semibold cursor-pointer transition-colors hover:bg-[rgba(239,68,68,0.18)]" onClick={()=>removeCategory(category.id)}>Delete</button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {category.templates.map((template) => (
                  <div key={template.id} className="grid grid-cols-[1.2fr_1.4fr_90px_180px_100px_auto_auto] gap-2 items-center max-xl:grid-cols-1">
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={template.title} onChange={(e)=>updateTemplateLocal(category.id, template.id, { title: e.target.value })} />
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={template.description} placeholder="Details" onChange={(e)=>updateTemplateLocal(category.id, template.id, { description: e.target.value })} />
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" type="number" value={template.sortOrder} onChange={(e)=>updateTemplateLocal(category.id, template.id, { sortOrder: e.target.value })} />
                    <select className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={template.defaultAssigneeId || ""} onChange={(e)=>updateTemplateLocal(category.id, template.id, { defaultAssigneeId: e.target.value })}>
                      <option value="">Default assignee</option>
                      {users.map((user)=><option key={user.id} value={user.id}>{user.name}</option>)}
                    </select>
                    <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" type="number" placeholder="Due +days" value={template.defaultDueOffsetDays} onChange={(e)=>updateTemplateLocal(category.id, template.id, { defaultDueOffsetDays: e.target.value })} />
                    <button className="self-end bg-[rgba(59,127,250,0.15)] border border-[rgba(59,127,250,0.3)] text-rc-accent-strong rounded-md px-4 py-1.5 text-xs font-semibold cursor-pointer transition-colors font-sans hover:bg-[rgba(59,127,250,0.25)]" onClick={()=>saveTemplate(template)}>Save</button>
                    <button className="bg-transparent border-0 text-[#2a3f60] cursor-pointer text-[11px] px-1.5 py-0.5 rounded transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]" onClick={()=>removeTemplate(template.id)}>✕</button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-[1.2fr_1.4fr_90px_180px_100px_auto_auto] gap-2 items-center max-xl:grid-cols-1">
                <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" placeholder="New template title" value={templateDrafts[category.id]?.title || ""} onChange={(e)=>setTemplateDrafts((prev)=>({ ...prev, [category.id]: { ...(prev[category.id] || {}), title: e.target.value } }))} />
                <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" placeholder="Details" value={templateDrafts[category.id]?.description || ""} onChange={(e)=>setTemplateDrafts((prev)=>({ ...prev, [category.id]: { ...(prev[category.id] || {}), description: e.target.value } }))} />
                <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" type="number" placeholder="Sort" value={templateDrafts[category.id]?.sortOrder || ""} onChange={(e)=>setTemplateDrafts((prev)=>({ ...prev, [category.id]: { ...(prev[category.id] || {}), sortOrder: e.target.value } }))} />
                <select className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" value={templateDrafts[category.id]?.defaultAssigneeId || ""} onChange={(e)=>setTemplateDrafts((prev)=>({ ...prev, [category.id]: { ...(prev[category.id] || {}), defaultAssigneeId: e.target.value } }))}>
                  <option value="">Default assignee</option>
                  {users.map((user)=><option key={user.id} value={user.id}>{user.name}</option>)}
                </select>
                <input className="bg-[#070e20] border border-[#14213d] rounded-md px-2.5 py-1.5 text-[12px] text-rc-text-primary outline-none font-sans transition-colors focus:border-rc-accent" type="number" placeholder="Due +days" value={templateDrafts[category.id]?.defaultDueOffsetDays || ""} onChange={(e)=>setTemplateDrafts((prev)=>({ ...prev, [category.id]: { ...(prev[category.id] || {}), defaultDueOffsetDays: e.target.value } }))} />
                <button className="bg-[rgba(59,127,250,0.12)] border border-[rgba(59,127,250,0.24)] text-rc-accent-strong rounded-md px-3.5 py-1.5 text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap hover:bg-[rgba(59,127,250,0.2)]" onClick={()=>createTemplate(category.id)}>Add Task</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
